import { createSelector } from 'reselect';
import { getFormValues, isInvalid } from 'redux-form';
import isEmpty from 'lodash/isEmpty';
import { initialState } from './reducer';
import { LEGAL_TABLE_ROWS_NAMES, LEGAL_FORM_NAME } from './constants';

export const selectLegalModalDomain = state => state.legalModalReducer || initialState;

export const selectRouterDomain = state => state.router || {};

export const getIsLegalInfoLoadingSelector = () =>
	createSelector(
		selectLegalModalDomain,
		({ isLegalInfoLoading, isLegalDeleting, isLegalUpdating }) =>
			isLegalInfoLoading || isLegalDeleting || isLegalUpdating,
	);

export const getLegalInfoSelector = () =>
	createSelector(selectLegalModalDomain, ({ legalInfo }) => {
		if (legalInfo.length === 0) {
			return [];
		}

		const data = {
			inn: legalInfo.inn,
			ogrn: legalInfo.ogrn,
			address: legalInfo.address,
			author: {
				name: `${legalInfo.user?.last_name || '-'} ${legalInfo.user?.first_name || '-'}`,
				role: legalInfo.user?.role.name || '-',
			},
			contact_group: { group: 'Контактные лица' },
			contact: legalInfo.contact,
		};

		return Object.entries(data).reduce((result, [key, value]) => {
			if (value.group) {
				result.push(value);
			} else {
				result.push({
					id: key,
					title: LEGAL_TABLE_ROWS_NAMES[key],
					value: value ?? '---',
					sub_value: value.sub_value ? value.sub_value : '',
				});
			}

			return result;
		}, []);
	});

export const getFormInitialValuesSelector = () =>
	createSelector(selectLegalModalDomain, getFormValues(LEGAL_FORM_NAME), ({ legalInfo }, formValues) => ({
		...legalInfo,
		...formValues,
	}));

export const getLegalNameSelector = () =>
	createSelector(selectLegalModalDomain, ({ legalInfo }) => legalInfo?.name || '');

export const getIsModalShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'legal');
	});

export const getIsAuditTabShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'legal' && query?.tab === 'audit');
	});

export const getActiveLegalIdSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => (query?.modal === 'legal' ? query?.id : null));

export const getIsLegalAddFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'legal' && query?.mode === 'add');
	});

export const getIsLegalEditFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'legal' && Boolean(query?.id) && query?.mode === 'edit');
	});

export const getLegalFormValuesSelector = () =>
	createSelector(getFormValues(LEGAL_FORM_NAME), formValues => formValues);

export const getLegalSearchValueSelector = () =>
	createSelector(selectLegalModalDomain, ({ legalSearchValue }) => legalSearchValue);

export const getIsLegalsOptionsLoadingSelector = () =>
	createSelector(selectLegalModalDomain, ({ isLegalsOptionsLoading }) => isLegalsOptionsLoading);

export const getLegalsSelector = () => createSelector(selectLegalModalDomain, ({ legalsOptions }) => legalsOptions);

export const getLegalsOptionsSelector = () =>
	createSelector(selectLegalModalDomain, ({ legalsOptions }) =>
		legalsOptions.map(legal => ({
			key: `${legal.data.inn}${legal.data.kpp}`,
			value: legal.data.inn,
			data: {
				title: legal.value,
				address: `${legal.data?.inn || ''} ${legal.data?.address?.value || ''}`,
				deleted: legal.data?.state?.status === 'LIQUIDATED',
			},
		})),
	);

export const getFormIsInvalidSelector = () => createSelector(isInvalid(LEGAL_FORM_NAME), isInvalid => isInvalid);
